/**
 * FC.04 — CriticidadNormativa
 *
 * Clasificador estático de criticidad por tipología PTEL.
 * La criticidad es un criterio operativo propio de PTEL Tool para ordenar
 * las ausencias: ninguna norma leída fija un inventario mínimo por tipología
 * (NORMA-b, 9-oct-2026; `.ptel/p0_norma_20261009/AUDIT_NORMA.md`).
 *
 * Uso: clasifica cada ausencia detectada por el MatchingEngine para
 * priorizar las infraestructuras que faltan en el documento PTEL.
 *
 * @module services/cotejo/CriticidadNormativa
 * @version 1.2.0  NORMA-b: fuera los artículos y las normas sin apoyo; las no leídas, ocultas
 */

import { InfrastructureType } from '../../types/infrastructure';
import { DESTINO_NIVEL_RIESGO } from '../../types/catastro';
import type { CriticidadLegal, ReferenciaNormativa } from './types';

// =============================================================================
// SUBTIPOS NORMATIVOS — OL-02 (INDUSTRIAL y FUEL)
// =============================================================================

/**
 * OL-02 — Subtipos normativos para InfrastructureType.INDUSTRIAL.
 * Se resuelven por destinoCat (Catastro) o por nombre del establecimiento.
 */
export type SubtipoNormativoIndustrial =
  | 'SEVESO_UMBRAL_SUPERIOR'   // RD 840/2015 — por encima del umbral superior
  | 'SEVESO_UMBRAL_INFERIOR'   // RD 840/2015 — entre umbrales
  | 'INDUSTRIAL_ALTO_RIESGO'   // IQU/IPE/IEX → legislación sectorial alta
  | 'INDUSTRIAL_MEDIO_RIESGO'  // IFB/IAG/IGR → riesgo moderado
  | 'INDUSTRIAL_GENERICO';     // INA/IAL/ITA → sin norma aplicable segura

/**
 * OL-02 — Subtipos normativos para InfrastructureType.FUEL.
 * Se resuelven por destinoCat (Catastro) o por nombre del establecimiento.
 */
export type SubtipoNormativoFuel =
  | 'GASOLINERA_CONVENCIONAL'  // RD 919/2006 (reglamento técnico CCPP)
  | 'ELECTROLINERA'            // RD 1053/2014
  | 'GLP_GNC_GNL'              // RD 919/2006 + Ley 34/1998
  | 'FUEL_GENERICO';           // Sin norma verificable

/** OL-02 — Unión de ambos subtipos. */
export type SubtipoNormativo = SubtipoNormativoIndustrial | SubtipoNormativoFuel;

// =============================================================================
// TABLA ESTÁTICA: tipología → referencia normativa
// =============================================================================

interface EntradaNormativa {
  readonly criticidad: CriticidadLegal;
  readonly norma: string;
  /** Solo se rellena con un artículo leído en la norma; hoy, vacío en toda la tabla (NORMA-b) */
  readonly articulo: string;
  /** OL-01 — Nivel de confianza de la referencia orientativa */
  readonly confianza: 'alta' | 'media' | 'baja';
}

/**
 * Mapa completo de tipologías PTEL a su criticidad legal.
 *
 * Criticidad:
 *   🔴 critica  — Servicios esenciales para la respuesta (sanitario, educativo, seguridad)
 *   🟠 alta     — Suministros y riesgos (agua, energía, residuos, industria)
 *   🟡 media    — Equipamientos complementarios
 *   🟢 baja     — Informativo
 * Es criterio de PTEL Tool, no cita de norma.
 *
 * Confianza:
 *   alta  — Norma y artículo leídos en el texto oficial → se muestra con artículo
 *   media — Norma leída, sin artículo → se muestra solo la norma
 *   baja  — No se muestra ni en P3 ni en el informe
 *
 * NORMA-b (9-oct-2026): `norma: ''` = retirada por no tener apoyo en el texto leído
 * (Decreto 69/2024, RD 524/2023). Norma con nombre y confianza 'baja' = pendiente
 * de lectura; se oculta hasta leerla. No subir la confianza sin haber leído la norma.
 */
/** OL-02 — Tabla normativa específica por subtipo INDUSTRIAL/FUEL. */
const NORMATIVA_SUBTIPO: Record<SubtipoNormativo, EntradaNormativa> = {
  // INDUSTRIAL
  SEVESO_UMBRAL_SUPERIOR:  { criticidad: 'alta', norma: 'RD 840/2015', articulo: '',         confianza: 'baja'  },
  SEVESO_UMBRAL_INFERIOR:  { criticidad: 'alta', norma: 'RD 840/2015', articulo: '',         confianza: 'baja'  },
  INDUSTRIAL_ALTO_RIESGO:  { criticidad: 'alta', norma: 'RD 840/2015', articulo: '',         confianza: 'baja'  },
  INDUSTRIAL_MEDIO_RIESGO: { criticidad: 'alta', norma: 'Ley 21/1992', articulo: '',         confianza: 'baja'  },
  INDUSTRIAL_GENERICO:     { criticidad: 'alta', norma: 'RD 840/2015', articulo: '',         confianza: 'baja'  },
  // FUEL
  GASOLINERA_CONVENCIONAL: { criticidad: 'alta', norma: 'RD 919/2006', articulo: '',         confianza: 'baja'  },
  ELECTROLINERA:           { criticidad: 'alta', norma: 'RD 1053/2014',articulo: '',         confianza: 'baja'  },
  GLP_GNC_GNL:             { criticidad: 'alta', norma: 'Ley 34/1998', articulo: '',         confianza: 'baja'  },
  FUEL_GENERICO:           { criticidad: 'alta', norma: 'RD 919/2006', articulo: '',         confianza: 'baja'  },
};

const NORMATIVA: Record<string, EntradaNormativa> = {
  // 🔴 Crítica — sin cita: el Decreto 69/2024 tiene 7 artículos y ninguno lista tipologías
  [InfrastructureType.HEALTH]:     { criticidad: 'critica', norma: '', articulo: '', confianza: 'baja' },
  [InfrastructureType.EDUCATION]:  { criticidad: 'critica', norma: '', articulo: '', confianza: 'baja' },
  [InfrastructureType.POLICE]:     { criticidad: 'critica', norma: '', articulo: '', confianza: 'baja' },
  [InfrastructureType.FIRE]:       { criticidad: 'critica', norma: '', articulo: '', confianza: 'baja' },
  [InfrastructureType.EMERGENCY]:  { criticidad: 'critica', norma: '', articulo: '', confianza: 'baja' },

  // 🟠 Alta — HYDRAULIC sin cita (RD 524/2023 leído entero: no trae tipologías);
  //    el resto, norma pendiente de lectura → oculta
  [InfrastructureType.HYDRAULIC]:  { criticidad: 'alta', norma: '',             articulo: '', confianza: 'baja' },
  [InfrastructureType.ENERGY]:     { criticidad: 'alta', norma: 'Ley 17/2015',  articulo: '', confianza: 'baja' },
  [InfrastructureType.RSU]:        { criticidad: 'alta', norma: 'Ley 7/2007',   articulo: '', confianza: 'baja' },
  [InfrastructureType.INDUSTRIAL]: { criticidad: 'alta', norma: 'RD 840/2015',  articulo: '', confianza: 'baja' },
  [InfrastructureType.FUEL]:       { criticidad: 'alta', norma: 'RD 840/2015',  articulo: '', confianza: 'baja' },

  // 🟡 Media — Decreto 69/2024 retirado; Ley 14/2007 y Ley 17/2015 pendientes de lectura → ocultas
  [InfrastructureType.SPORTS]:     { criticidad: 'media', norma: '',            articulo: '', confianza: 'baja' },
  [InfrastructureType.CULTURAL]:   { criticidad: 'media', norma: 'Ley 14/2007', articulo: '', confianza: 'baja' },
  [InfrastructureType.RELIGIOUS]:  { criticidad: 'media', norma: 'Ley 14/2007', articulo: '', confianza: 'baja' },
  [InfrastructureType.VIAL]:       { criticidad: 'media', norma: '',            articulo: '', confianza: 'baja' },
  [InfrastructureType.PORT]:       { criticidad: 'media', norma: '',            articulo: '', confianza: 'baja' },
  [InfrastructureType.TELECOM]:    { criticidad: 'media', norma: 'Ley 17/2015', articulo: '', confianza: 'baja' },
  [InfrastructureType.SOCIAL]:     { criticidad: 'media', norma: '',            articulo: '', confianza: 'baja' },

  // 🟢 Baja
  [InfrastructureType.MUNICIPAL]:  { criticidad: 'baja', norma: '', articulo: '', confianza: 'baja' },
  [InfrastructureType.GENERIC]:    { criticidad: 'baja', norma: '', articulo: '', confianza: 'baja' },
};

/** Criticidad por defecto para tipologías no mapeadas: sin cita */
const DEFAULT_ENTRY: EntradaNormativa = {
  criticidad: 'baja',
  norma: '',
  articulo: '',
  confianza: 'baja',
};

// =============================================================================
// CONSTANTES PÚBLICAS (OL-01)
// =============================================================================

/**
 * Texto de disclaimer estándar para badges normativos.
 * Debe mostrarse siempre que se muestre una referencia normativa en la UI.
 */
export const DISCLAIMER_NORMATIVO =
  'Referencia orientativa generada automáticamente por tipología. ' +
  'Puede no corresponder al subtipo específico. ' +
  'Verificar siempre antes de citar en documentación oficial.';

// =============================================================================
// API PÚBLICA
// =============================================================================

// =============================================================================
// OL-02 — resolverSubtipoNormativo
// =============================================================================

/**
 * OL-02 — Resuelve el subtipo normativo para INDUSTRIAL/FUEL.
 *
 * Prioridad:
 *   1. destinoCat (Catastro 3 letras) — señal más fiable
 *   2. Nombre del establecimiento  — heurística por palabras clave
 *   3. Fallback al subtipo genérico
 *
 * @param tipologia   InfrastructureType.INDUSTRIAL | FUEL
 * @param nombre      Nombre del establecimiento (texto libre del PTEL)
 * @param destinoCat  Destino catastral 3 letras (p. ej. 'IQU', 'EGS')
 */
export function resolverSubtipoNormativo(
  tipologia: InfrastructureType,
  nombre: string,
  destinoCat?: string,
): SubtipoNormativo {
  const n = nombre.toLowerCase();

  // ── FUEL ─────────────────────────────────────────────────────────────────
  if (tipologia === InfrastructureType.FUEL) {
    // P1: código catastral de gasolinera/estación de servicio
    if (destinoCat && ['EGS', 'EES'].includes(destinoCat)) return 'GASOLINERA_CONVENCIONAL';
    // P2: electrolinera (carga eléctrica)
    if (/electrolinera|carga.*electr|ev\s*charging/i.test(n)) return 'ELECTROLINERA';
    // P3: gas natural comprimido / licuado / propano
    if (/gnc|gnl|glp|autogas|gas\s*natural/i.test(n)) return 'GLP_GNC_GNL';
    // P4: gasolinera / estación de servicio por nombre
    if (/gasolinera|estaci[oó]n.*servicio|combustible|surtidor/i.test(n)) return 'GASOLINERA_CONVENCIONAL';
    return 'FUEL_GENERICO';
  }

  // ── INDUSTRIAL ───────────────────────────────────────────────────────────
  if (tipologia === InfrastructureType.INDUSTRIAL) {
    // P1: destino catastral
    if (destinoCat) {
      if (destinoCat === 'IQU') {
        // SEVESO superior solo si el nombre lo indica explícitamente
        if (/seveso|umbral\s*superior/i.test(n)) return 'SEVESO_UMBRAL_SUPERIOR';
        return 'SEVESO_UMBRAL_INFERIOR';
      }
      if (['IEN', 'IPE', 'IEX'].includes(destinoCat)) return 'INDUSTRIAL_ALTO_RIESGO';
      const nivel = DESTINO_NIVEL_RIESGO[destinoCat];
      if (nivel === 'ALTO') return 'INDUSTRIAL_ALTO_RIESGO';
      if (nivel === 'MEDIO') return 'INDUSTRIAL_MEDIO_RIESGO';
      return 'INDUSTRIAL_GENERICO';
    }
    // P2: por nombre (sin destinoCat)
    if (/seveso|sust.*peligr|qu[ií]mic|explosiv|inflamable|t[oó]xic/i.test(n)) return 'SEVESO_UMBRAL_INFERIOR';
    if (/petro|refiner[ií]a|cent.*el[eé]c|planta.*ciclo|subestaci[oó]n/i.test(n)) return 'INDUSTRIAL_ALTO_RIESGO';
    if (/bodega|almazara|silo|cooperativa|agroalim/i.test(n)) return 'INDUSTRIAL_MEDIO_RIESGO';
    return 'INDUSTRIAL_GENERICO';
  }

  // Tipología fuera de scope → genérico industrial (nunca debe ejecutarse)
  return 'INDUSTRIAL_GENERICO';
}

// =============================================================================
// API PÚBLICA
// =============================================================================

/**
 * Obtiene la referencia normativa completa para una tipología.
 *
 * OL-02: si tipología es INDUSTRIAL o FUEL, acepta `nombre` y `destinoCat`
 * para elevar la confianza mediante `resolverSubtipoNormativo`.
 * Totalmente retrocompatible: sin parámetros opcionales = idéntico a OL-01.
 */
export function getReferenciaNormativa(
  tipologia: InfrastructureType | string,
  nombre?: string,
  destinoCat?: string,
): ReferenciaNormativa {
  // OL-02 — resolución por subtipo para INDUSTRIAL/FUEL
  const esIndustrialFuel =
    tipologia === InfrastructureType.INDUSTRIAL ||
    tipologia === InfrastructureType.FUEL;

  if (esIndustrialFuel && nombre) {
    const subtipo = resolverSubtipoNormativo(
      tipologia as InfrastructureType,
      nombre,
      destinoCat,
    );
    const entry = NORMATIVA_SUBTIPO[subtipo];
    return {
      norma: entry.norma,
      articulo: entry.articulo,
      criticidad: entry.criticidad,
      confianza: entry.confianza,
    };
  }

  const entry = NORMATIVA[tipologia] ?? DEFAULT_ENTRY;
  return {
    norma: entry.norma,
    articulo: entry.articulo,
    criticidad: entry.criticidad,
    confianza: entry.confianza,
  };
}

/**
 * Obtiene solo la criticidad legal para una tipología.
 */
export function getCriticidad(tipologia: InfrastructureType | string): CriticidadLegal {
  return (NORMATIVA[tipologia] ?? DEFAULT_ENTRY).criticidad;
}

/**
 * OL-01 — Indica si debe mostrarse el badge normativo en la UI.
 * Retorna false cuando confianza === 'baja' (industrial, municipal, etc.).
 */
export function mostrarBadgeNormativo(tipologia: InfrastructureType | string): boolean {
  return getReferenciaNormativa(tipologia).confianza !== 'baja';
}

/**
 * Determina si una tipología es de criticidad crítica o alta.
 * Útil para filtrar rápidamente ausencias urgentes.
 */
export function esObligatoria(tipologia: InfrastructureType | string): boolean {
  const c = getCriticidad(tipologia);
  return c === 'critica' || c === 'alta';
}

/**
 * Devuelve todas las tipologías con una criticidad dada.
 */
export function getTipologiasPorCriticidad(criticidad: CriticidadLegal): string[] {
  return Object.entries(NORMATIVA)
    .filter(([, v]) => v.criticidad === criticidad)
    .map(([k]) => k);
}

/**
 * Devuelve el orden de prioridad para ordenar ausencias.
 * critica=0, alta=1, media=2, baja=3 (menor = más urgente).
 */
export function getOrdenPrioridad(criticidad: CriticidadLegal): number {
  const ORDEN: Record<CriticidadLegal, number> = { critica: 0, alta: 1, media: 2, baja: 3 };
  return ORDEN[criticidad] ?? 3;
}
